import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Info, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import CategoryPicker from '@/components/integrations/mapping/CategoryPicker'
import VaultMappingRow, {
  useVaultOptions,
} from '@/components/integrations/mapping/VaultMappingRow'
import AccountCategoryRow from '@/components/integrations/mapping/AccountCategoryRow'
import SimpleComboBox from '@/components/SimpleComboBox'
import type { XeroMappingOverview } from '@/services/api/integrations/useXeroMapping'
import {
  type Candidate,
  type FeedRow,
  useCandidates,
  useChooseEstimate,
  useConfirmLine,
  useCreateActual,
  useExcludeLine,
  useRefundEstimate,
  useRejectLine,
} from '@/services/api/integrations/useBankFeed'
import { localToday } from '@/services/api/integrations/useXeroMapping'
import { useAccountingProviderLabel } from '@/services/api/integrations/useAccountingProviderLabel'

export const money = (value: number | string) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const formatDate = (date: string) =>
  new Date(`${date.slice(0, 10)}T00:00:00`).toLocaleDateString()

/** The engine closes a transfer whose two sides map to the same vault. */
export const isSameVaultTransfer = (row: FeedRow) =>
  isTransfer(row.raw_transaction.type) &&
  row.match.status === 'excluded' &&
  row.match.matched_by === 'engine'

const isTransfer = (type: string) =>
  type === 'SPEND-TRANSFER' || type === 'RECEIVE-TRANSFER'

const CONFIDENCE_DOT: Record<Candidate['confidence'], string> = {
  high: 'bg-green-600',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
}

/** Inline right panel for one bank line: match it, categorise it, or map what's missing. */
export default function BankFeedPanel({
  row,
  mapping,
  canReconcile,
  unreconciledCategoryIds,
  onClose,
}: {
  row: FeedRow
  mapping?: XeroMappingOverview
  canReconcile: boolean
  unreconciledCategoryIds: Set<number>
  onClose: () => void
}) {
  const raw = row.raw_transaction
  const status = row.match.status
  const reconciled = status === 'confirmed' || status === 'manual'
  const provider = useAccountingProviderLabel()
  const bankAccount = mapping?.accounts.find(
    (a) => a.id === raw.external_account_id,
  )
  const counterpart = mapping?.accounts.find(
    (a) => a.id === raw.counterpart_account_id,
  )
  const exclude = useExcludeLine()
  const reject = useRejectLine()
  const confirm = useConfirmLine()

  const bankUnmapped = !!bankAccount && bankAccount.vault_mappings.length === 0
  const categoryUnmapped =
    !!counterpart &&
    raw.resolved_category_id !== null &&
    unreconciledCategoryIds.has(raw.resolved_category_id)

  return (
    <aside className="flex h-full min-w-0 flex-col border-l bg-white">
      <div className="flex items-start justify-between gap-2 border-b p-4">
        <div className="min-w-0">
          <p
            className={cn(
              'text-lg font-semibold',
              raw.direction === 'in' ? 'text-green-700' : 'text-red-700',
            )}
          >
            {raw.direction === 'in' ? '+' : '−'}
            {money(raw.amount)}
            {raw.is_foreign_currency && raw.currency_code && (
              <span className="ml-1 text-sm font-normal">
                {raw.currency_code}
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDate(raw.transaction_date)}
            {bankAccount && ` · ${bankAccount.name}`}
          </p>
          <p className="break-words text-sm">
            {raw.description || 'No description'}
          </p>
          {raw.reference && (
            <p className="text-xs text-muted-foreground">
              Ref: {raw.reference}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClose}
          aria-label="Close panel"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {row.match.provider_changed_at && (
          <Notice tone="warning">
            This line was edited in {provider} after it was reconciled. Confirm
            again to apply the new amount and date.
          </Notice>
        )}
        {raw.is_shadow && (
          <Notice tone="info">
            This line was voided or deleted in {provider}.
          </Notice>
        )}
        {raw.is_foreign_currency && (
          <Notice tone="warning">
            This line is in {raw.currency_code}. ProfitFlow can't reconcile
            other currencies yet — exclude it or record it yourself.
          </Notice>
        )}

        {/* Inline mapping (task 4, part 5): fix the cause, not just this line. */}
        {bankUnmapped && bankAccount && (
          <InlineMapping title="This bank account isn't mapped to a vault">
            <VaultMappingRow
              account={bankAccount}
              canEdit={!!mapping?.can.map_vaults}
              rawTransactionId={raw.id}
              startEditing
            />
          </InlineMapping>
        )}
        {categoryUnmapped && counterpart && (
          <InlineMapping
            title={`"${counterpart.name}" isn't mapped to a ${raw.direction === 'in' ? 'Money In' : 'Money Out'} category`}
          >
            <AccountCategoryRow
              account={counterpart}
              direction={raw.direction}
              contacts={mapping?.contacts ?? []}
              canEdit={!!mapping?.can.map_categories}
              rawTransactionId={raw.id}
            />
          </InlineMapping>
        )}

        {isOverpayment(raw.type) && canReconcile && (
          <OverpaymentChoice row={row} />
        )}

        {reconciled && !row.match.provider_changed_at ? (
          <ReconciledSummary row={row} />
        ) : status === 'excluded' && isSameVaultTransfer(row) ? (
          <p className="text-sm text-muted-foreground">
            Both sides of this transfer are in the same vault, so nothing moved
            in ProfitFlow and nothing is recorded.
          </p>
        ) : status === 'excluded' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This line is excluded and won't be reconciled.
            </p>
            {canReconcile && (
              <Button
                variant="outline"
                onClick={() => reject.mutate(raw.id)}
                disabled={reject.isPending}
              >
                Restore to pending
              </Button>
            )}
          </div>
        ) : isTransfer(raw.type) && !raw.paired_raw_transaction_id ? (
          <p className="text-sm text-muted-foreground">
            A transfer between two of your bank accounts in {provider}. Waiting
            for the other side to be imported (within two days, same amount) —
            or for both bank accounts to be mapped to vaults.
          </p>
        ) : isTransfer(raw.type) ? (
          <div className="space-y-3">
            <p className="text-sm">
              A transfer between two of your bank accounts in {provider}.
              Confirming records one transfer between their vaults.
            </p>
            {canReconcile && (
              <Button
                onClick={() => confirm.mutate(raw.id)}
                disabled={confirm.isPending}
              >
                Confirm transfer
              </Button>
            )}
          </div>
        ) : (
          <Tabs
            defaultValue={
              row.match.pf_transaction_id || status === 'suggested'
                ? 'match'
                : 'categorise'
            }
          >
            <TabsList className="mb-3">
              <TabsTrigger value="match">Match to estimate</TabsTrigger>
              <TabsTrigger value="categorise">Categorise manually</TabsTrigger>
            </TabsList>
            <TabsContent value="match">
              <MatchTab
                row={row}
                canReconcile={canReconcile && !raw.is_foreign_currency}
              />
            </TabsContent>
            <TabsContent value="categorise">
              <CategoriseTab
                row={row}
                mapping={mapping}
                canReconcile={canReconcile && !raw.is_foreign_currency}
                unreconciledCategoryIds={unreconciledCategoryIds}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      {canReconcile && !reconciled && status !== 'excluded' && (
        <div className="flex flex-wrap justify-end gap-2 border-t p-3">
          {status === 'suggested' && (
            <Button
              variant="ghost"
              size="md"
              onClick={() => reject.mutate(raw.id)}
              disabled={reject.isPending}
            >
              Reject suggestion
            </Button>
          )}
          <Button
            variant="destructiveGhost"
            size="md"
            onClick={() => exclude.mutate(raw.id)}
            disabled={exclude.isPending}
          >
            Exclude
          </Button>
        </div>
      )}
    </aside>
  )
}

const isOverpayment = (type: string) => /-(OVER|PRE)PAYMENT$/.test(type)

/**
 * An overpayment (or prepayment): money received/paid ahead of an invoice. Either
 * a refund is expected — forecast it — or it stays as a credit, which the provider
 * applies to a later invoice (that invoice's balance due, and forecast, drop).
 */
function OverpaymentChoice({ row }: { row: FeedRow }) {
  const raw = row.raw_transaction
  const refundDirection = raw.direction === 'in' ? 'out' : 'in'
  const vaults = useVaultOptions()
  const refund = useRefundEstimate()
  const provider = useAccountingProviderLabel()
  const [choice, setChoice] = useState<'refund' | 'credit' | null>(null)
  const [date, setDate] = useState(localToday())
  const [amount, setAmount] = useState(String(Math.abs(Number(raw.amount))))
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [vaultId, setVaultId] = useState<number | null>(raw.resolved_vault_id)

  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">
        {raw.direction === 'in'
          ? 'The customer overpaid.'
          : 'You overpaid the supplier.'}{' '}
        What happens to the extra?
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant={choice === 'refund' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setChoice('refund')}
        >
          Expect a refund
        </Button>
        <Button
          variant={choice === 'credit' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setChoice('credit')}
        >
          Keep as credit
        </Button>
      </div>
      {choice === 'credit' && (
        <p className="text-xs text-muted-foreground">
          Nothing else to do: when {provider} applies the credit to a later
          invoice, that invoice's forecast drops by it.
        </p>
      )}
      {choice === 'refund' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Refund expected on">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field label="Amount">
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Category">
            <CategoryPicker
              direction={refundDirection}
              value={categoryId}
              onChange={setCategoryId}
            />
          </Field>
          <Field label="Vault">
            <SimpleComboBox
              items={vaults}
              selected={vaults.find((v) => v.id === vaultId) ?? null}
              setSelected={(v: { id: number } | null) =>
                setVaultId(v?.id ?? null)
              }
              getValue={(v: { id: number; name: string }) =>
                `${v.name} #${v.id}`
              }
              getOptionLabel={(v: { name: string }) => v.name}
              getSelectedLabel={(v: { name: string }) => v.name}
              placeholder="Choose a vault"
            />
          </Field>
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={
                !date ||
                Number(amount) <= 0 ||
                !categoryId ||
                !vaultId ||
                refund.isPending
              }
              onClick={() =>
                categoryId &&
                vaultId &&
                refund.mutate({
                  rawId: raw.id,
                  transaction_date: date,
                  amount: Number(amount),
                  vault_id: vaultId,
                  transaction_category_id: categoryId,
                })
              }
            >
              Forecast refund
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function Notice({
  tone,
  children,
}: {
  tone: 'warning' | 'info'
  children: React.ReactNode
}) {
  const Icon = tone === 'warning' ? AlertTriangle : Info
  return (
    <div
      className={cn(
        'flex gap-2 rounded-md border p-3 text-sm',
        tone === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'bg-layout-secondary',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div>{children}</div>
    </div>
  )
}

function InlineMapping({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md border border-amber-200 p-3">
      <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
        <AlertTriangle className="size-4" /> {title}
      </p>
      {children}
    </div>
  )
}

function ReconciledSummary({ row }: { row: FeedRow }) {
  const t = row.suggested_transaction
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium text-green-700">Reconciled</p>
      {t && (
        <p className="text-muted-foreground">
          {formatDate(t.transaction_date)} · {t.vault_name} ·{' '}
          {t.category_name ?? 'Transfer'}
          {t.description ? ` · ${t.description}` : ''}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {row.match.matched_by === 'engine'
          ? 'Matched automatically'
          : 'Matched by a person'}
      </p>
    </div>
  )
}

function MatchTab({
  row,
  canReconcile,
}: {
  row: FeedRow
  canReconcile: boolean
}) {
  const raw = row.raw_transaction
  const [days, setDays] = useState(30)
  const { data, isPending } = useCandidates(raw.id, days)
  const candidates = data?.data ?? []
  const [selected, setSelected] = useState<number | null>(null)
  const confirm = useConfirmLine()
  const choose = useChooseEstimate()

  // Pre-select the engine's suggestion once per line and window, so a
  // background refetch never overrides the user's pick.
  const preselectedFor = useRef<string | null>(null)
  useEffect(() => {
    const key = `${raw.id}:${days}`
    if (!data || preselectedFor.current === key) return
    preselectedFor.current = key
    setSelected(data.data.find((c) => c.is_suggested)?.transaction_id ?? null)
  }, [data, raw.id, days])

  const chosen = candidates.find((c) => c.transaction_id === selected)
  const windowEnd = new Date(`${raw.transaction_date.slice(0, 10)}T00:00:00`)
  windowEnd.setDate(windowEnd.getDate() + days)

  const handleMatch = () => {
    if (!chosen) return
    if (chosen.is_suggested && row.match.status === 'suggested')
      confirm.mutate(raw.id)
    else choose.mutate({ rawId: raw.id, transactionId: chosen.transaction_id })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>Estimates up to {windowEnd.toLocaleDateString()}</span>
        <label className="flex items-center gap-1">
          Window
          <select
            className="rounded border bg-white px-1 py-0.5"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[7, 30, 60, 90].map((d) => (
              <option key={d} value={d}>
                ±{d} days
              </option>
            ))}
          </select>
        </label>
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Looking for estimates…</p>
      ) : candidates.length === 0 ? (
        <p className="rounded-md border p-3 text-sm text-muted-foreground">
          No estimates match this transaction. Use{' '}
          <strong>Categorise manually</strong> to record it instead.
        </p>
      ) : (
        <ul
          className="divide-y rounded-md border"
          role="radiogroup"
          aria-label="Estimates"
        >
          {candidates.map((c) => (
            <li key={c.transaction_id}>
              <label className="flex cursor-pointer items-start gap-3 p-3 hover:bg-layout-secondary">
                <input
                  type="radio"
                  name="candidate"
                  className="mt-1"
                  checked={selected === c.transaction_id}
                  onChange={() => setSelected(c.transaction_id)}
                  disabled={!canReconcile}
                />
                <span className="min-w-0 flex-1 text-sm">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        'size-2 rounded-full',
                        CONFIDENCE_DOT[c.confidence],
                      )}
                      title={`${c.confidence} confidence`}
                    />
                    <Badge variant="outline">
                      {c.source === 'xero_invoice' ? 'Invoice' : 'Estimate'}
                    </Badge>
                    <span className="font-medium">{money(c.amount)}</span>
                    {c.is_suggested && <Badge>Suggested</Badge>}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Dated {formatDate(c.transaction_date)} · {c.vault_name}
                    {c.category_name ? ` · ${c.category_name}` : ''}
                  </span>
                  {c.description && (
                    <span className="block truncate text-xs">
                      {c.description}
                    </span>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {chosen && Math.abs(chosen.variance) >= 0.005 && (
        <Notice tone="warning">
          Estimate was {money(chosen.amount)} — actual is {money(raw.amount)} (
          {chosen.variance > 0 ? '+' : '−'}
          {money(Math.abs(chosen.variance))}). Matching updates the transaction
          to the actual amount and date.
        </Notice>
      )}

      {canReconcile && (
        <div className="flex justify-end">
          <Button
            onClick={handleMatch}
            disabled={!chosen || confirm.isPending || choose.isPending}
          >
            Match
          </Button>
        </div>
      )}
    </div>
  )
}

function CategoriseTab({
  row,
  mapping,
  canReconcile,
  unreconciledCategoryIds,
}: {
  row: FeedRow
  mapping?: XeroMappingOverview
  canReconcile: boolean
  unreconciledCategoryIds: Set<number>
}) {
  const raw = row.raw_transaction
  const vaults = useVaultOptions()
  const create = useCreateActual()
  const mappedCategory =
    raw.resolved_category_id !== null &&
    !unreconciledCategoryIds.has(raw.resolved_category_id)
      ? raw.resolved_category_id
      : null
  const [categoryId, setCategoryId] = useState<number | null>(mappedCategory)
  const [vaultId, setVaultId] = useState<number | null>(raw.resolved_vault_id)
  const [date, setDate] = useState(raw.transaction_date.slice(0, 10))
  const [amount, setAmount] = useState(String(Math.abs(Number(raw.amount))))
  const [description, setDescription] = useState(raw.description ?? '')
  const [contactId, setContactId] = useState<number | null>(raw.contact_id)
  const contact = useMemo(
    () => mapping?.contacts.find((c) => c.id === contactId),
    [mapping, contactId],
  )

  // A contact picked here applies that contact's override for this account, if one exists.
  const applyContact = (contactId: number) => {
    setContactId(contactId)
    const account = mapping?.accounts.find(
      (a) => a.id === raw.counterpart_account_id,
    )
    const override = account?.contact_category_mappings
      .filter(
        (m) =>
          m.external_contact_id === contactId && m.direction === raw.direction,
      )
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]
    if (override) setCategoryId(override.pf_category_id)
  }

  const valid = !!categoryId && !!vaultId && !!date && Number(amount) > 0

  return (
    <div className="space-y-3">
      {mappedCategory && (
        <Notice tone="info">
          Category and vault pre-filled from your account mapping.
        </Notice>
      )}
      <Field label="Category *">
        <CategoryPicker
          direction={raw.direction}
          value={categoryId}
          onChange={setCategoryId}
          disabled={!canReconcile}
        />
      </Field>
      <Field label="Vault *">
        <SimpleComboBox
          items={vaults}
          selected={vaults.find((v) => v.id === vaultId) ?? null}
          setSelected={(v: { id: number } | null) => setVaultId(v?.id ?? null)}
          getValue={(v: { id: number; name: string }) => `${v.name} #${v.id}`}
          getOptionLabel={(v: { name: string }) => v.name}
          getSelectedLabel={(v: { name: string }) => v.name}
          placeholder="Choose a vault"
          disabled={!canReconcile}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date *">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={!canReconcile}
          />
        </Field>
        <Field label="Amount *">
          <Input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={
              raw.direction === 'in' ? 'text-green-700' : 'text-red-700'
            }
            disabled={!canReconcile}
          />
        </Field>
      </div>
      {mapping && mapping.contacts.length > 0 && (
        <Field label="Contact">
          <SimpleComboBox
            items={mapping.contacts}
            selected={contact ?? null}
            setSelected={(c: { id: number } | null) => c && applyContact(c.id)}
            getValue={(c: { id: number; name: string }) => `${c.name} #${c.id}`}
            getOptionLabel={(c: { name: string }) => c.name}
            getSelectedLabel={(c: { name: string }) => c.name}
            placeholder="Optional"
            disabled={!canReconcile}
          />
        </Field>
      )}
      <Field label="Description">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!canReconcile}
        />
      </Field>
      {canReconcile && (
        <div className="flex justify-end">
          <Button
            disabled={!valid || create.isPending}
            onClick={() =>
              categoryId &&
              vaultId &&
              create.mutate({
                rawId: raw.id,
                vault_id: vaultId,
                transaction_category_id: categoryId,
                transaction_date: date,
                amount: Number(amount),
                description: description || null,
              })
            }
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="mb-1 text-xs text-muted-foreground">{label}</p>
      {children}
    </div>
  )
}
